import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Play, Search, Star, Calendar, Clock, ChevronLeft, ChevronRight,
  Film, Grid, List, Filter, X, Loader2, Tv, Info, PlayCircle, Copy, Check, ExternalLink
} from 'lucide-react';
import Hls from 'hls.js';

// Use relative URLs so it works regardless of domain
const apiCall = async (endpoint, options = {}) => {
  const response = await fetch(`/api${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  return response.json();
};

const TVShowsSection = () => {
  // State
  const [series, setSeries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [seriesDetail, setSeriesDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentEpisode, setCurrentEpisode] = useState(null);
  const [streamError, setStreamError] = useState('');
  const [directUrl, setDirectUrl] = useState('');
  const [copied, setCopied] = useState(false);
  
  // Refs
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  
  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await apiCall('/iptv/series/categories');
        if (data.status === 'success' && data.categories) {
          setCategories(data.categories);
        }
      } catch (error) {
        console.error('Error fetching series categories:', error);
      }
    };
    fetchCategories();
  }, []);
  
  // Fetch series
  useEffect(() => {
    const fetchSeries = async () => {
      setLoading(true);
      try {
        let endpoint = '/iptv/series?limit=200';
        if (selectedCategory !== 'all') {
          endpoint += `&category_id=${selectedCategory}`;
        }
        if (searchQuery) {
          endpoint += `&search=${encodeURIComponent(searchQuery)}`;
        }
        
        const data = await apiCall(endpoint);
        if (data.status === 'success') {
          setSeries(data.series || []);
        }
      } catch (error) {
        console.error('Error fetching series:', error);
      } finally {
        setLoading(false);
      }
    };
    
    const debounceTimer = setTimeout(fetchSeries, 300);
    return () => clearTimeout(debounceTimer);
  }, [selectedCategory, searchQuery]);
  
  // Fetch series details when selected
  useEffect(() => {
    const fetchSeriesDetail = async () => {
      if (!selectedSeries) {
        setSeriesDetail(null);
        return;
      }
      
      setDetailLoading(true);
      try {
        const data = await apiCall(`/iptv/series/${selectedSeries.id}`);
        if (data.status === 'success') {
          setSeriesDetail(data);
          // Auto-select first season
          if (data.seasons && data.seasons.length > 0) {
            setSelectedSeason(data.seasons[0].season_number);
          }
        }
      } catch (error) {
        console.error('Error fetching series details:', error);
      } finally {
        setDetailLoading(false);
      }
    };
    
    fetchSeriesDetail();
  }, [selectedSeries]);
  
  // Play episode
  const playEpisode = useCallback(async (episode) => {
    if (!selectedSeries) return;
    
    setCurrentEpisode(episode);
    setIsPlaying(true);
    setStreamError('');
    setDirectUrl('');
    setCopied(false);
    
    try {
      const streamData = await apiCall(`/iptv/series/stream/${selectedSeries.id}/${episode.id}`);
      
      if (streamData.status === 'success') {
        // Save direct URL for copy functionality
        if (streamData.direct_url) {
          setDirectUrl(streamData.direct_url);
        }
        
        if (videoRef.current) {
          const video = videoRef.current;
          
          // Cleanup previous HLS instance
          if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
          }
          
          // Try home transcoder first, fall back to server transcoding
          // Home transcoder runs on user's Mac and has direct IPTV access
          const homeTranscodeUrl = `/api/iptv/series/home-transcode/${episode.id}/playlist.m3u8`;
          const serverTranscodeUrl = `/api/iptv/series/transcode/${episode.id}/playlist.m3u8`;
          
          // Check if home transcoder is available
          let transcodeUrl = homeTranscodeUrl;
          try {
            const healthCheck = await fetch('/api/iptv/home-transcoder/health');
            const health = await healthCheck.json();
            if (health.status !== 'connected') {
              console.log('Home transcoder not available, falling back to server transcoding');
              transcodeUrl = serverTranscodeUrl;
            } else {
              console.log('Using home transcoder for playback');
            }
          } catch (e) {
            console.log('Home transcoder check failed, using server transcoding');
            transcodeUrl = serverTranscodeUrl;
          }
          
          if (Hls.isSupported()) {
            const hls = new Hls({
              enableWorker: true,
              lowLatencyMode: false,
              backBufferLength: 30,
              maxBufferLength: 60,
              fragLoadingMaxRetry: 5,
              manifestLoadingMaxRetry: 5,
              fragLoadingTimeOut: 30000,
              manifestLoadingTimeOut: 30000,
              levelLoadingTimeOut: 30000,
            });
            
            hls.loadSource(transcodeUrl);
            hls.attachMedia(video);
            
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
              video.play().catch(e => console.log('Autoplay prevented:', e));
            });
            
            hls.on(Hls.Events.ERROR, (event, data) => {
              console.log('HLS error:', data.type, data.details, data.fatal);
              if (data.fatal) {
                // Check home transcoder status to see if we should wait
                apiCall(`/iptv/series/home-transcode/${episode.id}/status`).then(status => {
                  console.log('Transcode status:', status);
                  if (status.status === 'transcoding' && status.segments_ready > 0) {
                    setStreamError(`Video is being prepared... ${status.segments_ready} segments ready. Try playing again in a moment.`);
                  } else if (status.status === 'transcoding') {
                    setStreamError('Video is being prepared for playback. This may take a minute for the first time. Please wait...');
                  } else if (status.status === 'ready') {
                    // Transcoding is done but playback failed - codec issue
                    setStreamError('Stream codec not supported by browser. Copy the URL below to watch in VLC.');
                  } else if (status.status === 'error') {
                    setStreamError('Failed to prepare video. Copy the URL below to watch in VLC.');
                  } else if (status.status === 'unreachable') {
                    setStreamError('Home transcoder not running. Start it on your Mac and try again.');
                  } else {
                    setStreamError('Stream not available. Copy the URL below to watch in VLC.');
                  }
                }).catch(e => {
                  console.error('Status check failed:', e);
                  setStreamError('Stream error. Copy the URL below to watch in an external player.');
                });
                
                hls.destroy();
                hlsRef.current = null;
              }
            });
            
            hlsRef.current = hls;
          } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
            // Safari native HLS
            video.src = transcodeUrl;
            video.play().catch(e => console.log('Autoplay prevented:', e));
          } else {
            // No HLS support, show fallback
            setStreamError('Your browser does not support HLS video playback. Copy the URL below to watch in VLC.');
          }
        }
      } else {
        setStreamError('Failed to get stream URL.');
      }
    } catch (error) {
      console.error('Error playing episode:', error);
      setStreamError('Failed to load stream. Please try again.');
    }
  }, [selectedSeries]);
  
  // Close player
  const closePlayer = () => {
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    setIsPlaying(false);
    setCurrentEpisode(null);
    setStreamError('');
    setDirectUrl('');
    setCopied(false);
  };
  
  // Close detail modal
  const closeDetailModal = () => {
    setSelectedSeries(null);
    setSeriesDetail(null);
    setSelectedSeason(null);
    closePlayer();
  };
  
  // Get episodes for selected season
  const getSeasonEpisodes = () => {
    if (!seriesDetail || !selectedSeason) return [];
    return seriesDetail.episodes[selectedSeason] || [];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Tv className="text-cyan-400" />
            TV Shows
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            {series.length} shows available
          </p>
        </div>
        
        {/* Controls */}
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search shows..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 w-64"
              data-testid="tv-shows-search"
            />
          </div>
          
          {/* View Mode Toggle */}
          <div className="flex items-center bg-white/5 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-cyan-500/30 text-cyan-400' : 'text-gray-400 hover:text-white'}`}
            >
              <Grid size={18} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-cyan-500/30 text-cyan-400' : 'text-gray-400 hover:text-white'}`}
            >
              <List size={18} />
            </button>
          </div>
        </div>
      </div>
      
      {/* Category Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-white/10">
        <button
          onClick={() => setSelectedCategory('all')}
          className={`px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
            selectedCategory === 'all'
              ? 'bg-cyan-500/30 text-cyan-400 border border-cyan-500/50'
              : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
          }`}
        >
          All Shows
        </button>
        {categories.slice(0, 15).map((category) => (
          <button
            key={category.category_id}
            onClick={() => setSelectedCategory(category.category_id)}
            className={`px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
              selectedCategory === category.category_id
                ? 'bg-cyan-500/30 text-cyan-400 border border-cyan-500/50'
                : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
            }`}
          >
            {category.category_name}
          </button>
        ))}
      </div>
      
      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
          <span className="ml-3 text-gray-400">Loading TV shows...</span>
        </div>
      )}
      
      {/* Grid View */}
      {!loading && viewMode === 'grid' && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {series.map((show) => (
            <div
              key={show.id}
              onClick={() => setSelectedSeries(show)}
              className="group relative bg-white/5 rounded-xl overflow-hidden border border-white/10 hover:border-cyan-500/50 cursor-pointer transition-all hover:scale-105 hover:shadow-[0_0_30px_rgba(6,182,212,0.2)]"
              data-testid={`tv-show-card-${show.id}`}
            >
              {/* Poster */}
              <div className="aspect-[2/3] relative">
                {show.cover ? (
                  <img
                    src={show.cover}
                    alt={show.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className={`absolute inset-0 bg-gradient-to-br from-purple-900 to-black flex items-center justify-center ${show.cover ? 'hidden' : ''}`}
                >
                  <Tv size={48} className="text-gray-600" />
                </div>
                
                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <PlayCircle size={48} className="text-cyan-400" />
                </div>
                
                {/* Rating badge */}
                {show.rating && (
                  <div className="absolute top-2 right-2 px-2 py-1 bg-black/70 rounded flex items-center gap-1">
                    <Star size={12} className="text-yellow-400 fill-yellow-400" />
                    <span className="text-white text-xs">{show.rating}</span>
                  </div>
                )}
              </div>
              
              {/* Info */}
              <div className="p-3">
                <h3 className="text-white font-medium text-sm truncate">{show.name}</h3>
                {show.genre && (
                  <p className="text-gray-500 text-xs truncate mt-1">{show.genre}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* List View */}
      {!loading && viewMode === 'list' && (
        <div className="space-y-2">
          {series.map((show) => (
            <div
              key={show.id}
              onClick={() => setSelectedSeries(show)}
              className="flex items-center gap-4 p-3 bg-white/5 rounded-xl border border-white/10 hover:border-cyan-500/50 cursor-pointer transition-all group"
            >
              {/* Thumbnail */}
              <div className="w-16 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-gray-800">
                {show.cover ? (
                  <img src={show.cover} alt={show.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Tv size={24} className="text-gray-600" />
                  </div>
                )}
              </div>
              
              {/* Info */}
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-medium truncate">{show.name}</h3>
                {show.genre && <p className="text-gray-500 text-sm truncate">{show.genre}</p>}
                {show.plot && <p className="text-gray-400 text-xs line-clamp-2 mt-1">{show.plot}</p>}
              </div>
              
              {/* Rating */}
              {show.rating && (
                <div className="flex items-center gap-1 px-3 py-1 bg-white/5 rounded">
                  <Star size={14} className="text-yellow-400 fill-yellow-400" />
                  <span className="text-white text-sm">{show.rating}</span>
                </div>
              )}
              
              {/* Play Button */}
              <button className="p-3 bg-cyan-500/20 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                <Play size={20} className="text-cyan-400" />
              </button>
            </div>
          ))}
        </div>
      )}
      
      {/* Empty State */}
      {!loading && series.length === 0 && (
        <div className="text-center py-20">
          <Tv size={48} className="text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No TV shows found</p>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="mt-4 px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30"
            >
              Clear search
            </button>
          )}
        </div>
      )}
      
      {/* Series Detail Modal */}
      {selectedSeries && (
        <div className="fixed inset-0 z-[9999] bg-black/95 overflow-y-auto">
          <div className="min-h-screen">
            {/* Close Button */}
            <button
              onClick={closeDetailModal}
              className="fixed top-4 right-4 z-[10000] p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
              data-testid="close-series-detail"
            >
              <X size={24} className="text-white" />
            </button>
            
            {detailLoading ? (
              <div className="flex items-center justify-center h-screen">
                <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
              </div>
            ) : seriesDetail ? (
              <div>
                {/* Hero Banner */}
                <div className="relative h-[50vh] overflow-hidden">
                  {seriesDetail.info.cover && (
                    <img
                      src={seriesDetail.info.cover}
                      alt={seriesDetail.info.name}
                      className="w-full h-full object-cover"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-transparent" />
                  
                  {/* Info Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <h1 className="text-4xl font-bold text-white mb-2">{seriesDetail.info.name}</h1>
                    <div className="flex items-center gap-4 text-gray-300 text-sm mb-4">
                      {seriesDetail.info.genre && <span>{seriesDetail.info.genre}</span>}
                      {seriesDetail.info.release_date && (
                        <span className="flex items-center gap-1">
                          <Calendar size={14} />
                          {seriesDetail.info.release_date}
                        </span>
                      )}
                      {seriesDetail.info.rating && (
                        <span className="flex items-center gap-1">
                          <Star size={14} className="text-yellow-400 fill-yellow-400" />
                          {seriesDetail.info.rating}
                        </span>
                      )}
                      {seriesDetail.seasons && (
                        <span>{seriesDetail.seasons.length} Season{seriesDetail.seasons.length !== 1 ? 's' : ''}</span>
                      )}
                    </div>
                    {seriesDetail.info.plot && (
                      <p className="text-gray-300 max-w-3xl line-clamp-3">{seriesDetail.info.plot}</p>
                    )}
                  </div>
                </div>
                
                {/* Seasons & Episodes */}
                <div className="p-8">
                  {/* Season Tabs */}
                  {seriesDetail.seasons && seriesDetail.seasons.length > 0 && (
                    <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
                      {seriesDetail.seasons.map((season) => (
                        <button
                          key={season.season_number}
                          onClick={() => setSelectedSeason(season.season_number)}
                          className={`px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
                            selectedSeason === season.season_number
                              ? 'bg-cyan-500/30 text-cyan-400 border border-cyan-500/50'
                              : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
                          }`}
                        >
                          Season {season.season_number}
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* Episodes List */}
                  <div className="grid gap-3">
                    {getSeasonEpisodes().map((episode) => (
                      <div
                        key={episode.id}
                        className="flex items-center gap-4 p-4 bg-white/5 rounded-xl border border-white/10 hover:border-cyan-500/50 transition-all group"
                      >
                        {/* Episode Thumbnail */}
                        <div className="w-40 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-gray-800 relative">
                          {episode.info?.movie_image ? (
                            <img
                              src={episode.info.movie_image}
                              alt={episode.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900/50 to-black">
                              <Film size={24} className="text-gray-600" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <PlayCircle size={32} className="text-white" />
                          </div>
                        </div>
                        
                        {/* Episode Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-cyan-400 text-sm font-medium">E{episode.episode_num}</span>
                            <h4 className="text-white font-medium truncate">{episode.title || `Episode ${episode.episode_num}`}</h4>
                          </div>
                          {episode.info?.plot && (
                            <p className="text-gray-400 text-sm line-clamp-2 mt-1">{episode.info.plot}</p>
                          )}
                          <div className="flex items-center gap-3 mt-2 text-gray-500 text-xs">
                            {episode.info?.duration && (
                              <span className="flex items-center gap-1">
                                <Clock size={12} />
                                {episode.info.duration}
                              </span>
                            )}
                            {episode.info?.releasedate && (
                              <span>{episode.info.releasedate}</span>
                            )}
                          </div>
                        </div>
                        
                        {/* Play Button */}
                        <button
                          onClick={() => playEpisode(episode)}
                          className="p-3 bg-cyan-500 rounded-full hover:bg-cyan-400 transition-colors"
                          data-testid={`play-episode-${episode.id}`}
                        >
                          <Play size={20} className="text-white" fill="white" />
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  {/* No Episodes Message */}
                  {getSeasonEpisodes().length === 0 && selectedSeason && (
                    <div className="text-center py-10">
                      <Film size={48} className="text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-400">No episodes available for this season</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-screen">
                <p className="text-gray-400">Failed to load series details</p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Video Player Modal */}
      {isPlaying && currentEpisode && (
        <div className="fixed inset-0 z-[10001] bg-black flex items-center justify-center">
          {/* Close Button */}
          <button
            onClick={closePlayer}
            className="absolute top-4 right-4 z-[10002] p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
          >
            <X size={24} className="text-white" />
          </button>
          
          {/* Episode Info */}
          <div className="absolute top-4 left-4 z-10">
            <h3 className="text-white font-bold">{selectedSeries?.name}</h3>
            <p className="text-gray-400 text-sm">
              Season {selectedSeason} • Episode {currentEpisode.episode_num}
            </p>
          </div>
          
          {/* Video */}
          <video
            ref={videoRef}
            controls
            autoPlay
            className="w-full h-full"
          />
          
          {/* Stream Error Overlay */}
          {streamError && (
            <div className="absolute inset-0 bg-black/90 flex items-center justify-center">
              <div className="text-center p-6 max-w-md">
                <Tv size={48} className="text-cyan-500 mx-auto mb-4" />
                <h4 className="text-white font-semibold mb-2">Stream Unavailable</h4>
                <p className="text-gray-400 mb-4 text-sm">{streamError}</p>
                
                {directUrl && (
                  <div className="mb-4">
                    <p className="text-gray-500 text-xs mb-2">Direct stream URL:</p>
                    <div className="flex items-center gap-2 bg-white/5 rounded-lg p-2">
                      <input
                        type="text"
                        value={directUrl}
                        readOnly
                        className="flex-1 bg-transparent text-gray-300 text-xs truncate outline-none"
                        data-testid="direct-url-input"
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(directUrl);
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                        }}
                        className="p-2 bg-cyan-600 rounded hover:bg-cyan-700 transition-colors"
                        data-testid="copy-url-btn"
                      >
                        {copied ? <Check size={16} className="text-white" /> : <Copy size={16} className="text-white" />}
                      </button>
                    </div>
                    
                    <div className="flex gap-2 mt-3 justify-center flex-wrap">
                      {/* Copy & Open VLC manually - most reliable */}
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(directUrl);
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                          // Try to open VLC protocol
                          window.location.href = `vlc://${directUrl}`;
                        }}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 rounded-lg text-white text-sm hover:bg-orange-700 transition-colors"
                        data-testid="open-vlc-btn"
                      >
                        <ExternalLink size={16} />
                        Open in VLC
                      </button>
                      
                      {/* IINA Protocol (Mac video player) */}
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(directUrl);
                          window.location.href = `iina://weblink?url=${encodeURIComponent(directUrl)}`;
                        }}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 rounded-lg text-white text-sm hover:bg-purple-700 transition-colors"
                        data-testid="open-iina-btn"
                      >
                        <ExternalLink size={16} />
                        Open in IINA
                      </button>
                      
                      {/* Open in new tab - works everywhere */}
                      <a 
                        href={directUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 rounded-lg text-white text-sm hover:bg-blue-700 transition-colors"
                        data-testid="open-direct-btn"
                      >
                        <ExternalLink size={16} />
                        Open Direct
                      </a>
                    </div>
                    
                    <p className="text-gray-600 text-xs mt-3">
                      URL copied! Paste in VLC: File → Open Network Stream
                    </p>
                  </div>
                )}
                
                <button
                  onClick={closePlayer}
                  className="px-4 py-2 bg-white/10 rounded-lg text-white hover:bg-white/20"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TVShowsSection;
